import { images } from "../../../Assets/assetsHelper";
import "./Pricing.css";
import React from "react";
import { Typography, Grid } from "@mui/material";
import config from "../../../config";
import { PulseLoader } from "react-spinners";

function Pricing({ categories, loading, profile }) {
  const formatAmount = (amountCents) => {
    return (amountCents / 100).toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  };

  const renderCategoryPrices = (category) => {
    const { categoryPrice } = category;
    if (!categoryPrice) return null;

    const items = [];

    // Team prices - combine all currencies
    if (categoryPrice.teamPrices && categoryPrice.teamPrices.length > 0) {
      const priceStrings = categoryPrice.teamPrices.map(
        (price) => `${formatAmount(price.amountCents)} ${price.currencyCode}`
      );
      items.push(<li key="team">Per Team: {priceStrings.join(", ")}</li>);
    }

    // Member prices - combine all currencies
    if (categoryPrice.memberPrices && categoryPrice.memberPrices.length > 0) {
      const priceStrings = categoryPrice.memberPrices.map(
        (price) => `${formatAmount(price.amountCents)} ${price.currencyCode}`
      );
      items.push(<li key="member">Per Member: {priceStrings.join(", ")}</li>);
    }

    // Members group prices - group by complete price set and combine member counts with same prices
    if (
      categoryPrice.membersGroupPrices &&
      categoryPrice.membersGroupPrices.length > 0
    ) {
      // Create a helper function to create a normalized key from prices
      const createPriceKey = (prices) => {
        return prices
          .map((p) => `${p.amountCents}-${p.currencyCode}`)
          .sort()
          .join("|");
      };

      // Group member counts by their complete price set
      const priceGroups = {};
      categoryPrice.membersGroupPrices.forEach((groupPrice) => {
        if (
          groupPrice.membersGroupCountPrice &&
          groupPrice.membersGroupCountPrice.length > 0
        ) {
          const priceKey = createPriceKey(groupPrice.membersGroupCountPrice);
          if (!priceGroups[priceKey]) {
            priceGroups[priceKey] = {
              prices: groupPrice.membersGroupCountPrice,
              counts: [],
            };
          }
          if (!priceGroups[priceKey].counts.includes(groupPrice.count)) {
            priceGroups[priceKey].counts.push(groupPrice.count);
          }
        }
      });

      // Convert to array, sort by minimum member count, then sort counts within each group, and create list items
      const sortedGroups = Object.values(priceGroups).sort((a, b) => {
        const minA = Math.min(...a.counts);
        const minB = Math.min(...b.counts);
        return minA - minB;
      });

      sortedGroups.forEach((group, index) => {
        group.counts.sort((a, b) => a - b);
        const countsString =
          group.counts.length === 1
            ? `${group.counts[0]} members`
            : group.counts.length === 2
            ? `${group.counts[0]} and ${group.counts[1]} members`
            : `${group.counts.slice(0, -1).join(", ")}, and ${
                group.counts[group.counts.length - 1]
              } members`;

        const priceStrings = group.prices.map(
          (price) => `${formatAmount(price.amountCents)} ${price.currencyCode}`
        );

        items.push(
          <li key={`group-${index}`}>
            Team with {countsString}: {priceStrings.join(", ")}
          </li>
        );
      });
    }

    return items;
  };

  if (loading) {
    return (
      <div className="mainContainer">
        <div className="pricingCont">
          <div className="loadingBox">
            <PulseLoader color={config.PRIMARY_COLOR} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainContainer">
      <div className="pricingCont">
        <div className="greyBoxVer">
          <Typography
            sx={{
              color: "primary.main",
              fontSize: { xs: "28px", md: "35px" },
              fontWeight: "500",
              marginBottom: "5px",
            }}
          >
            Welcome to the Pricing Page
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            Thank you for your interest in the{" "}
            <strong>
              {config.APP_NAME == "Code Reg"
                ? "Code Challenge Competition"
                : config.APP_NAME == "WeDo Reg"
                ? "WeDo Challenge Competition"
                : "International Robot Challenge Competition"}
            </strong>
            ! We've designed a simple and flexible pricing structure to
            accommodate teams of all sizes. This page provides all the details
            you need about registration fees, what's included, and the rules for
            team structure. We're excited to have you participate and look
            forward to supporting you throughout the competition journey.
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "16px" },
              marginTop: "8px",
            }}
          >
            If you have any questions, feel free to reach out to us.
          </Typography>
        </div>

        {categories && categories.length > 0 ? (
          <Grid container spacing={2}>
            {categories.map((category) => (
              <Grid item xs={12} md={6} key={category.id}>
                <div className="greyBoxVer" style={{ margin: "0" }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "20px", md: "22px" },
                      fontWeight: "600",
                      marginBottom: "10px",
                    }}
                  >
                    {category.name} Registration Fees
                  </Typography>
                  <ul style={{ marginLeft: "30px", marginBottom: "10px" }}>
                    {renderCategoryPrices(category)}
                  </ul>
                  {category.deadline && (
                    <>
                      <Typography
                        sx={{
                          fontSize: { xs: "20px", md: "22px" },
                          fontWeight: "600",
                          marginTop: "20px",
                          marginBottom: "10px",
                        }}
                      >
                        Registration & Payment Details:
                      </Typography>
                      <ul style={{ marginLeft: "30px" }}>
                        <li>
                          All registration details and payments must be
                          completed by {formatDate(category.deadline)}.
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div className="greyBoxVer">
            <Typography
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              No pricing information available at this time.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default Pricing;
